import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Dev → Express on 5001 | Production → Vercel serverless function
const API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5001/api/send-email'
  : '/api/send-email';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Phone input CSS (scoped to .btml-phone) ────────────────────────────────
const PHONE_STYLES = `
  .btml-phone .PhoneInput {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 0;
    background: transparent;
    border: none;
  }
  .btml-phone .PhoneInputCountry {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-right: 14px;
    margin-right: 14px;
    border-right: 1px solid var(--border);
    flex-shrink: 0;
    position: relative;
    cursor: pointer;
  }
  .btml-phone .PhoneInputCountrySelect {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 1;
    width: 100%;
    height: 100%;
  }
  .btml-phone .PhoneInputCountryIcon {
    width: 24px;
    height: 18px;
    border-radius: 3px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .btml-phone .PhoneInputCountryIcon--square {
    width: 24px;
    height: 18px;
  }
  .btml-phone .PhoneInputCountryIconImg {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .btml-phone .PhoneInputCountrySelectArrow {
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid var(--text3);
    margin-left: 2px;
    flex-shrink: 0;
  }
  .btml-phone .PhoneInputInput {
    flex: 1;
    background: transparent;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    font-size: 16px;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-weight: 400;
    color: var(--text);
    padding: 14px 0;
    min-width: 0;
  }
  .btml-phone .PhoneInputInput::placeholder {
    color: var(--text3);
    opacity: 1;
  }
`;

// ── Page definitions ───────────────────────────────────────────────────────
const PAGES = [
  { id: 1, title: 'Identity',      label: 'Get Started' },
  { id: 2, title: 'Qualification', label: 'About You' },
  { id: 3, title: 'Intent',        label: 'Your Interest' },
  { id: 4, title: 'Goal',          label: 'Your Goal' },
  { id: 5, title: 'Confirmation',  label: 'All Set' },
];

const TOTAL_PAGES = PAGES.length;

const BUSINESS_OPTIONS = [
  'Broker / Agent',
  'Developer / Builder',
  'Both',
  'Other',
];

const LEADS_OPTIONS = [
  'Under 10',
  '10–50',
  '50–100',
  '100–500',
  '500+',
  'Other',
];

const INTEREST_OPTIONS = [
  { value: 'AI CRM', label: 'AI CRM', desc: 'Manage & convert leads' },
  { value: 'Lead Generation Platform', label: 'Lead Generation Platform', desc: 'Find new buyers' },
  { value: 'Both', label: 'Both', desc: 'Full suite — CRM + Lead Platform' },
];

const GOAL_OPTIONS = [
  'Get more property buyers',
  'Improve lead conversion',
  'Automate follow-ups',
  'Scale my sales team',
  'Just exploring',
];

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  businessType: '',
  businessTypeOther: '',
  leadsVolume: '',
  leadsVolumeOther: '',
  interest: '',
  primaryGoal: '',
  painPoint: '',
};

// ── Validation helpers ─────────────────────────────────────────────────────
function isPage1Valid(fd) {
  if (!fd.name.trim()) return false;
  try { if (!fd.phone || !isValidPhoneNumber(fd.phone)) return false; } catch { return false; }
  if (!EMAIL_RE.test(fd.email)) return false;
  return true;
}

function isPage2Valid(fd) {
  if (!fd.businessType) return false;
  if (fd.businessType === 'Other' && !fd.businessTypeOther.trim()) return false;
  if (!fd.leadsVolume) return false;
  if (fd.leadsVolume === 'Other') {
    if (!fd.leadsVolumeOther.trim()) return false;
    if (!/^\d+$/.test(fd.leadsVolumeOther.trim())) return false;
  }
  return true;
}

function isPage3Valid(fd) {
  return !!fd.interest;
}

function isPage4Valid(fd) {
  return !!fd.primaryGoal;
}

function isPageValid(pageIndex, fd) {
  switch (pageIndex) {
    case 0: return isPage1Valid(fd);
    case 1: return isPage2Valid(fd);
    case 2: return isPage3Valid(fd);
    case 3: return isPage4Valid(fd);
    default: return true;
  }
}

function getPage1Errors(fd) {
  const errors = {};
  if (!fd.name.trim()) errors.name = 'Please enter your name.';
  if (!fd.phone) errors.phone = 'Please enter your phone number.';
  else { try { if (!isValidPhoneNumber(fd.phone)) errors.phone = 'Enter a valid number for the selected country.'; } catch { errors.phone = 'Please enter a valid phone number.'; } }
  if (!fd.email.trim()) errors.email = 'Please enter your email address.';
  else if (!EMAIL_RE.test(fd.email)) errors.email = 'Please enter a valid email address.';
  return errors;
}

function getPage2Errors(fd) {
  const errors = {};
  if (!fd.businessType) errors.businessType = 'Please select an option.';
  else if (fd.businessType === 'Other' && !fd.businessTypeOther.trim()) errors.businessTypeOther = 'Please specify your business.';
  if (!fd.leadsVolume) errors.leadsVolume = 'Please select an option.';
  else if (fd.leadsVolume === 'Other') {
    if (!fd.leadsVolumeOther.trim()) errors.leadsVolumeOther = 'Please enter an approximate number.';
    else if (!/^\d+$/.test(fd.leadsVolumeOther.trim())) errors.leadsVolumeOther = 'Please enter digits only.';
  }
  return errors;
}

// ── Dynamic copy helper ────────────────────────────────────────────────────
function getDynamicTagline(fd) {
  if (fd.businessType === 'Developer / Builder') return 'Find high-intent buyers faster';
  if (fd.businessType === 'Broker / Agent') return 'Close more deals with AI CRM';
  if (fd.businessType === 'Both') return 'The complete AI platform for real estate';
  return 'AI-powered tools to grow your business';
}

// ── Animation variants ─────────────────────────────────────────────────────
const overlayVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.28, ease: 'easeOut' } },
  exit:    { opacity: 0, transition: { duration: 0.22, ease: 'easeIn' } },
};

const cardVariants = {
  hidden:  { opacity: 0, scale: 0.95, y: 12 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.38, ease: [0.16, 1, 0.3, 1] } },
  exit:    { opacity: 0, scale: 0.97, y: 6,  transition: { duration: 0.2, ease: 'easeIn' } },
};

const pageVariants = {
  enter:  (dir) => ({ opacity: 0, x: dir > 0 ? 44 : -44 }),
  center: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
  exit:   (dir) => ({ opacity: 0, x: dir > 0 ? -24 : 24, transition: { duration: 0.22, ease: 'easeIn' } }),
};

const confirmVariants = {
  hidden:  { opacity: 0, scale: 0.94 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1], staggerChildren: 0.09 } },
};

const confirmChildVariants = {
  hidden:  { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.48, ease: [0.16, 1, 0.3, 1] } },
};

const fieldFadeIn = {
  hidden:  { opacity: 0, y: 10 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.35, ease: [0.16, 1, 0.3, 1] } }),
};

// ── Main Component ─────────────────────────────────────────────────────────
export default function BookTrialModal({ isOpen, onClose, onNavigate }) {
  const [page, setPage] = useState(0);   // 0-indexed (0=Identity, 1=Qualification, 2=Intent, 3=Goal, 4=Confirmation)
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const isLastFormPage = page === 3;   // Page 4 (Goal) is last before confirm
  const canProceed = isPageValid(page, formData);
  const showAlreadySubmitted = hasSubmitted && !isComplete;

  // Scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (window.lenis) window.lenis.stop();
    } else {
      document.body.style.overflow = '';
      if (window.lenis) window.lenis.start();
    }
    return () => {
      document.body.style.overflow = '';
      if (window.lenis) window.lenis.start();
    };
  }, [isOpen]);

  // Reset validation on page change
  useEffect(() => {
    setShowValidation(false);
    setFieldErrors({});
    setApiError(null);
  }, [page]);

  const resetModal = () => {
    setPage(0);
    setFormData(EMPTY_FORM);
    setIsComplete(false);
    setApiError(null);
    setDirection(1);
    setIsSubmitting(false);
    setShowValidation(false);
    setFieldErrors({});
    // hasSubmitted intentionally NOT reset — persists across open/close
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetModal, 350);
  };

  const doSubmit = async (data) => {
    if (isSubmitting || hasSubmitted) return;
    setIsSubmitting(true);
    setApiError(null);
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          phone: data.phone,
          email: data.email,
          business_type: data.businessType === 'Other' ? data.businessTypeOther : data.businessType,
          leads_volume: data.leadsVolume === 'Other' ? data.leadsVolumeOther : data.leadsVolume,
          interest: data.interest,
          primary_goal: data.primaryGoal,
          pain_point: data.painPoint || '',
        }),
      });
      if (res.ok) {
        setHasSubmitted(true);
        setIsComplete(true);
        setPage(4); // Move to confirmation page
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } catch {
      setApiError('Connection error. Please check your network.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (isSubmitting) return;

    // Validate current page
    if (!canProceed) {
      setShowValidation(true);
      if (page === 0) setFieldErrors(getPage1Errors(formData));
      if (page === 1) setFieldErrors(getPage2Errors(formData));
      return;
    }

    if (isLastFormPage) {
      // Submit from Page 4 (Goal)
      await doSubmit(formData);
    } else {
      setDirection(1);
      setPage((p) => p + 1);
    }
  };

  const handleBack = () => {
    if (page === 0) { handleClose(); return; }
    setApiError(null);
    setShowValidation(false);
    setFieldErrors({});
    setDirection(-1);
    setPage((p) => p - 1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isSubmitting) handleNext();
  };

  const updateField = (field, value) => {
    setFormData((d) => ({ ...d, [field]: value }));
    if (showValidation) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // ── Progress bar (pages 1–4 only, not confirmation) ──
  const progressPages = 4;
  const progressFraction = Math.min(page + 1, progressPages) / progressPages;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="book-trial-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10002,
            background: 'rgba(8, 8, 20, 0.72)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <style>{PHONE_STYLES}</style>

          <motion.div
            variants={cardVariants}
            style={{
              width: '100%',
              maxWidth: 540,
              background: '#ffffff',
              borderRadius: 24,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 40px 100px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.04)',
            }}
          >
            {/* ── TOP BAR ── */}
            <div style={{ padding: '22px 24px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
              {!isComplete && !showAlreadySubmitted && (
                <>
                  {/* Continuous progress bar */}
                  <div style={{ flex: 1, height: 3, borderRadius: 100, background: 'var(--bg3)', overflow: 'hidden' }}>
                    <motion.div
                      initial={false}
                      animate={{ scaleX: progressFraction }}
                      transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
                      style={{ height: '100%', width: '100%', background: 'var(--blue)', transformOrigin: 'left', borderRadius: 100 }}
                    />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: '0.06em', textTransform: 'uppercase', minWidth: 32, textAlign: 'right' }}>
                    {Math.min(page + 1, progressPages)}/{progressPages}
                  </span>
                </>
              )}
              <motion.button
                whileHover={{ scale: 1.08, background: 'var(--bg3)' }}
                whileTap={{ scale: 0.93 }}
                onClick={handleClose}
                style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--bg2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 17, lineHeight: 1, flexShrink: 0, transition: 'background 0.18s' }}
              >
                ×
              </motion.button>
            </div>

            {/* ── BODY ── */}
            <div style={{ position: 'relative', minHeight: isComplete || showAlreadySubmitted ? 'auto' : 480, overflow: 'hidden' }}>
              {showAlreadySubmitted ? (
                <AlreadySubmittedScreen handleClose={handleClose} />
              ) : isComplete ? (
                <ConfirmationScreen handleClose={handleClose} onNavigate={onNavigate} formData={formData} />
              ) : (
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={page}
                    custom={direction}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    style={{ padding: '32px 28px 28px', display: 'flex', flexDirection: 'column', minHeight: 480 }}
                  >
                    {/* Page label */}
                    <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--blue)', marginBottom: 6 }}>
                      {PAGES[page].label}
                    </p>

                    {/* ─── PAGE 1: Identity ─── */}
                    {page === 0 && (
                      <Page1Identity
                        formData={formData}
                        updateField={updateField}
                        fieldErrors={showValidation ? fieldErrors : {}}
                        handleKeyDown={handleKeyDown}
                      />
                    )}

                    {/* ─── PAGE 2: Qualification ─── */}
                    {page === 1 && (
                      <Page2Qualification
                        formData={formData}
                        updateField={updateField}
                        fieldErrors={showValidation ? fieldErrors : {}}
                        handleKeyDown={handleKeyDown}
                      />
                    )}

                    {/* ─── PAGE 3: Intent ─── */}
                    {page === 2 && (
                      <Page3Intent
                        formData={formData}
                        updateField={updateField}
                        onAutoAdvance={() => { setDirection(1); setPage((p) => p + 1); }}
                      />
                    )}

                    {/* ─── PAGE 4: Goal / Pain Point ─── */}
                    {page === 3 && (
                      <Page4Goal
                        formData={formData}
                        updateField={updateField}
                        handleKeyDown={handleKeyDown}
                      />
                    )}

                    {/* API error */}
                    <AnimatePresence>
                      {apiError && <ErrorMessage key="api-err" message={apiError} />}
                    </AnimatePresence>

                    <div style={{ flex: 1, minHeight: 16 }} />

                    {/* ── Navigation buttons ── */}
                    {/* Pages 0,1,3 always show nav. Page 2 shows nav only if interest is selected (for back). */}
                    {(page !== 2 || formData.interest) && (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 8 }}>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={handleBack}
                          className="btn-secondary"
                          style={{ padding: '13px 20px', fontSize: 14, minHeight: 44, fontWeight: 500 }}
                        >
                          {page === 0 ? 'Cancel' : '← Back'}
                        </motion.button>

                        {/* Hide Continue on page 2 — auto-advance on select */}
                        {page !== 2 && (
                          <motion.button
                            whileHover={canProceed && !isSubmitting ? { scale: 1.02, boxShadow: '0 0 24px rgba(79,82,248,0.22)' } : {}}
                            whileTap={canProceed && !isSubmitting ? { scale: 0.98 } : {}}
                            onClick={handleNext}
                            disabled={isSubmitting}
                            className="btn-primary"
                            style={{
                              flex: 1,
                              padding: '13px 20px',
                              fontSize: 14,
                              minHeight: 44,
                              opacity: (canProceed && !isSubmitting) ? 1 : 0.45,
                              cursor: (canProceed && !isSubmitting) ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              transition: 'opacity 0.22s',
                            }}
                          >
                            {isSubmitting ? <SpinnerIcon /> : isLastFormPage ? 'Submit →' : 'Continue →'}
                          </motion.button>
                        )}
                      </div>
                    )}

                    {/* Back link for page 2 when nothing selected yet */}
                    {page === 2 && !formData.interest && (
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={handleBack}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', marginTop: 12, padding: '6px 0', display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        ← Go back
                      </motion.button>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>

            {/* ── BOTTOM BRAND STRIP ── */}
            {!isComplete && !showAlreadySubmitted && (
              <div style={{ padding: '12px 28px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)' }} />
                <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500, letterSpacing: '0.01em' }}>
                  {page === 0
                    ? "We'll only use this to reach out about your request"
                    : 'LEAD-AI · Secure & RERA Compliant'}
                </span>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 1 — Identity (name, phone, email on one screen)
// ═══════════════════════════════════════════════════════════════════════════
function Page1Identity({ formData, updateField, fieldErrors, handleKeyDown }) {
  return (
    <>
      <h2 style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: 400, lineHeight: 1.25, color: 'var(--text)', marginBottom: 24 }}>
        Let's start with the basics
      </h2>

      {/* Name */}
      <motion.div custom={0} variants={fieldFadeIn} initial="hidden" animate="visible" style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
          Your Name
        </label>
        <TextInput
          type="text"
          inputMode="text"
          placeholder="Full name"
          value={formData.name}
          onChange={(val) => updateField('name', val)}
          onKeyDown={handleKeyDown}
          validationState={fieldErrors.name ? 'error' : null}
          compact
        />
        <AnimatePresence>
          {fieldErrors.name && <ErrorMessage key="name-err" message={fieldErrors.name} />}
        </AnimatePresence>
      </motion.div>

      {/* Phone */}
      <motion.div custom={1} variants={fieldFadeIn} initial="hidden" animate="visible" style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
          Phone Number
        </label>
        <PhoneInputField
          value={formData.phone}
          onChange={(val) => updateField('phone', val || '')}
          validationState={fieldErrors.phone ? 'error' : null}
          compact
        />
        <AnimatePresence>
          {fieldErrors.phone && <ErrorMessage key="phone-err" message={fieldErrors.phone} />}
        </AnimatePresence>
      </motion.div>

      {/* Email */}
      <motion.div custom={2} variants={fieldFadeIn} initial="hidden" animate="visible" style={{ marginBottom: 0 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
          Email Address
        </label>
        <TextInput
          type="email"
          inputMode="email"
          placeholder="you@company.com"
          value={formData.email}
          onChange={(val) => updateField('email', val)}
          onKeyDown={handleKeyDown}
          validationState={fieldErrors.email ? 'error' : null}
          compact
        />
        <AnimatePresence>
          {fieldErrors.email && <ErrorMessage key="email-err" message={fieldErrors.email} />}
        </AnimatePresence>
      </motion.div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 2 — Qualification (business type + leads volume)
// ═══════════════════════════════════════════════════════════════════════════
function Page2Qualification({ formData, updateField, fieldErrors, handleKeyDown }) {
  const isBusinessOther = formData.businessType === 'Other';
  const isLeadsOther = formData.leadsVolume === 'Other';

  return (
    <>
      <h2 style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: 400, lineHeight: 1.25, color: 'var(--text)', marginBottom: 24 }}>
        Tell us about your business
      </h2>

      {/* Business type */}
      <motion.div custom={0} variants={fieldFadeIn} initial="hidden" animate="visible" style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
          What best describes your business?
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {BUSINESS_OPTIONS.map((opt) => (
            <ChipButton
              key={opt}
              label={opt}
              selected={formData.businessType === opt}
              onClick={() => updateField('businessType', opt)}
            />
          ))}
        </div>
        <AnimatePresence>
          {isBusinessOther && (
            <motion.div
              key="biz-other"
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <TextInput
                type="text"
                inputMode="text"
                placeholder="Please specify your business"
                value={formData.businessTypeOther}
                onChange={(val) => updateField('businessTypeOther', val)}
                onKeyDown={handleKeyDown}
                validationState={fieldErrors.businessTypeOther ? 'error' : null}
                compact
                autoFocus
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {(fieldErrors.businessType || fieldErrors.businessTypeOther) && (
            <ErrorMessage key="biz-err" message={fieldErrors.businessType || fieldErrors.businessTypeOther} />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Leads volume */}
      <motion.div custom={1} variants={fieldFadeIn} initial="hidden" animate="visible">
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
          How many leads do you handle monthly?
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {LEADS_OPTIONS.map((opt) => (
            <ChipButton
              key={opt}
              label={opt}
              selected={formData.leadsVolume === opt}
              onClick={() => updateField('leadsVolume', opt)}
            />
          ))}
        </div>
        <AnimatePresence>
          {isLeadsOther && (
            <motion.div
              key="leads-other"
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <TextInput
                type="text"
                inputMode="numeric"
                placeholder="Approximate monthly lead count"
                value={formData.leadsVolumeOther}
                onChange={(val) => updateField('leadsVolumeOther', val)}
                onKeyDown={handleKeyDown}
                validationState={fieldErrors.leadsVolumeOther ? 'error' : null}
                compact
                autoFocus
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {(fieldErrors.leadsVolume || fieldErrors.leadsVolumeOther) && (
            <ErrorMessage key="leads-err" message={fieldErrors.leadsVolume || fieldErrors.leadsVolumeOther} />
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 3 — Intent (CRM / Lead Platform / Both)
// ═══════════════════════════════════════════════════════════════════════════
function Page3Intent({ formData, updateField, onAutoAdvance }) {
  const handleSelect = (value) => {
    updateField('interest', value);
    // Auto-advance after selection with brief delay for visual feedback
    setTimeout(() => {
      onAutoAdvance();
    }, 350);
  };

  return (
    <>
      <h2 style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: 400, lineHeight: 1.25, color: 'var(--text)', marginBottom: 6 }}>
        What are you looking for?
      </h2>
      <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 24, lineHeight: 1.5 }}>
        Select the solution that fits your needs
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {INTEREST_OPTIONS.map((opt, i) => (
          <motion.button
            key={opt.value}
            custom={i}
            variants={fieldFadeIn}
            initial="hidden"
            animate="visible"
            whileHover={{ scale: 1.015, borderColor: 'var(--blue)' }}
            whileTap={{ scale: 0.985 }}
            onClick={() => handleSelect(opt.value)}
            style={{
              width: '100%',
              padding: '16px 18px',
              border: `1.5px solid ${formData.interest === opt.value ? 'var(--blue)' : 'var(--border)'}`,
              borderRadius: 14,
              background: formData.interest === opt.value ? 'rgba(79,82,248,0.05)' : '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              minHeight: 56,
              transition: 'border-color 0.18s, background 0.18s',
              fontFamily: 'inherit',
              textAlign: 'left',
            }}
          >
            <motion.div
              animate={{ borderColor: formData.interest === opt.value ? 'var(--blue)' : '#d1d5db', background: formData.interest === opt.value ? 'var(--blue)' : 'transparent' }}
              transition={{ duration: 0.18 }}
              style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <AnimatePresence>
                {formData.interest === opt.value && (
                  <motion.div
                    key="dot"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.15, ease: 'backOut' }}
                    style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }}
                  />
                )}
              </AnimatePresence>
            </motion.div>
            <div>
              <div style={{ fontSize: 14, fontWeight: formData.interest === opt.value ? 600 : 500, color: formData.interest === opt.value ? 'var(--blue)' : 'var(--text)' }}>
                {opt.label}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                {opt.desc}
              </div>
            </div>
            {opt.value === 'Both' && (
              <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#10b981', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.18)', padding: '3px 8px', borderRadius: 100 }}>
                Best Value
              </span>
            )}
          </motion.button>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 4 — Goal / Pain Point (primary goal + optional text)
// ═══════════════════════════════════════════════════════════════════════════
function Page4Goal({ formData, updateField, handleKeyDown }) {
  return (
    <>
      <h2 style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: 400, lineHeight: 1.25, color: 'var(--text)', marginBottom: 6 }}>
        What's your primary goal right now?
      </h2>
      <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.5 }}>
        This helps us tailor the best solution for you
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {GOAL_OPTIONS.map((opt, i) => (
          <motion.button
            key={opt}
            custom={i}
            variants={fieldFadeIn}
            initial="hidden"
            animate="visible"
            whileHover={{ scale: 1.015, borderColor: 'var(--blue)' }}
            whileTap={{ scale: 0.985 }}
            onClick={() => updateField('primaryGoal', opt)}
            style={{
              width: '100%',
              padding: '13px 16px',
              border: `1.5px solid ${formData.primaryGoal === opt ? 'var(--blue)' : 'var(--border)'}`,
              borderRadius: 12,
              background: formData.primaryGoal === opt ? 'rgba(79,82,248,0.05)' : '#ffffff',
              color: formData.primaryGoal === opt ? 'var(--blue)' : 'var(--text)',
              fontSize: 14,
              fontWeight: formData.primaryGoal === opt ? 600 : 400,
              fontFamily: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minHeight: 46,
              transition: 'border-color 0.18s, background 0.18s, color 0.18s',
            }}
          >
            <motion.div
              animate={{ borderColor: formData.primaryGoal === opt ? 'var(--blue)' : '#d1d5db', background: formData.primaryGoal === opt ? 'var(--blue)' : 'transparent' }}
              transition={{ duration: 0.18 }}
              style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <AnimatePresence>
                {formData.primaryGoal === opt && (
                  <motion.div
                    key="dot"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.15, ease: 'backOut' }}
                    style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }}
                  />
                )}
              </AnimatePresence>
            </motion.div>
            {opt}
          </motion.button>
        ))}
      </div>

      {/* Optional pain-point text */}
      <motion.div
        custom={5}
        variants={fieldFadeIn}
        initial="hidden"
        animate="visible"
      >
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', letterSpacing: '0.04em', display: 'block', marginBottom: 6 }}>
          Anything specific you're struggling with? <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span>
        </label>
        <textarea
          placeholder="e.g. leads going cold, manual follow-ups..."
          value={formData.painPoint}
          onChange={(e) => updateField('painPoint', e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleKeyDown(e); } }}
          rows={2}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: 14,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontWeight: 400,
            color: 'var(--text)',
            background: 'var(--bg2)',
            border: '1.5px solid var(--border)',
            borderRadius: 12,
            outline: 'none',
            resize: 'none',
            minHeight: 56,
            boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--blue)'; e.target.style.boxShadow = '0 0 0 4px rgba(79,82,248,0.10)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
        />
      </motion.div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Already Submitted Screen
// ═══════════════════════════════════════════════════════════════════════════
function AlreadySubmittedScreen({ handleClose }) {
  return (
    <motion.div
      variants={confirmVariants}
      initial="hidden"
      animate="visible"
      style={{ padding: '44px 28px 36px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <motion.div
        variants={confirmChildVariants}
        style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(79,82,248,0.07)', border: '1.5px solid rgba(79,82,248,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}
      >
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <circle cx="14" cy="10" r="4" fill="var(--blue)" />
          <path d="M6 24c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="var(--blue)" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </motion.div>
      <motion.h2
        variants={confirmChildVariants}
        style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: 400, lineHeight: 1.2, color: 'var(--text)', marginBottom: 12 }}
      >
        Request already sent
      </motion.h2>
      <motion.p
        variants={confirmChildVariants}
        style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.65, maxWidth: 320, marginBottom: 32 }}
      >
        Our team will reach out to you shortly. Please check your inbox for a confirmation email.
      </motion.p>
      <motion.button
        variants={confirmChildVariants}
        whileHover={{ scale: 1.02, boxShadow: '0 0 22px rgba(79,82,248,0.2)' }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClose}
        className="btn-primary"
        style={{ justifyContent: 'center', padding: '13px 32px', minHeight: 44 }}
      >
        Got it
      </motion.button>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Confirmation Screen (Page 5 — premium feel with dynamic copy)
// ═══════════════════════════════════════════════════════════════════════════
function ConfirmationScreen({ handleClose, onNavigate, formData }) {
  const tagline = getDynamicTagline(formData);

  // Dynamic CTA labels based on interest
  const exploreCrmLabel = formData.interest === 'Lead Generation Platform' ? 'See Lead Platform' : 'Explore CRM';
  const showSecondaryCta = formData.interest === 'Both' || formData.interest === 'Lead Generation Platform';

  return (
    <motion.div
      variants={confirmVariants}
      initial="hidden"
      animate="visible"
      style={{ padding: '44px 28px 36px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      {/* Animated checkmark */}
      <motion.div
        variants={confirmChildVariants}
        style={{ width: 76, height: 76, borderRadius: '50%', background: 'rgba(16,185,129,0.07)', border: '1.5px solid rgba(16,185,129,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}
      >
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <motion.path
            d="M7 15.5L12.5 21L23 10"
            stroke="#10b981"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.25 }}
          />
        </svg>
      </motion.div>

      <motion.h2
        variants={confirmChildVariants}
        style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 'clamp(24px, 3.5vw, 32px)', fontWeight: 400, lineHeight: 1.15, color: 'var(--text)', marginBottom: 8 }}
      >
        You're all set!
      </motion.h2>

      {/* Dynamic tagline based on business type */}
      <motion.p
        variants={confirmChildVariants}
        style={{ fontSize: 14, color: 'var(--blue)', fontWeight: 600, marginBottom: 12 }}
      >
        {tagline}
      </motion.p>

      <motion.p
        variants={confirmChildVariants}
        style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.65, maxWidth: 360, marginBottom: 12 }}
      >
        Our team will reach out shortly.
      </motion.p>

      <motion.p
        variants={confirmChildVariants}
        style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.55, maxWidth: 340, marginBottom: 32, fontStyle: 'italic' }}
      >
        We'll review your requirements and get back with the best setup for your business.
      </motion.p>

      <motion.div
        variants={confirmChildVariants}
        style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 320 }}
      >
        <motion.button
          whileHover={{ scale: 1.02, boxShadow: '0 0 22px rgba(79,82,248,0.2)' }}
          whileTap={{ scale: 0.98 }}
          onClick={() => { handleClose(); if (onNavigate) onNavigate('crm'); }}
          className="btn-primary lg"
          style={{ justifyContent: 'center', width: '100%' }}
        >
          {exploreCrmLabel} →
        </motion.button>
        {showSecondaryCta && onNavigate && (
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => { handleClose(); onNavigate('leads'); }}
            className="btn-secondary"
            style={{ width: '100%', justifyContent: 'center', padding: '13px 24px', fontSize: 14, minHeight: 44 }}
          >
            See Lead Platform →
          </motion.button>
        )}
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', padding: '8px 0', marginTop: 4 }}
        >
          Back to Home
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Shared sub-components
// ═══════════════════════════════════════════════════════════════════════════

// ── PhoneInputField ────────────────────────────────────────────────────────
function PhoneInputField({ value, onChange, validationState, compact }) {
  const [focused, setFocused] = React.useState(false);

  const borderColor = validationState === 'error' ? '#ef4444'
    : validationState === 'valid' ? '#10b981'
    : focused ? 'var(--blue)' : 'var(--border)';

  const shadow = validationState === 'error' ? '0 0 0 4px rgba(239,68,68,0.10)'
    : validationState === 'valid' ? '0 0 0 4px rgba(16,185,129,0.10)'
    : focused ? '0 0 0 4px rgba(79,82,248,0.10)' : 'none';

  return (
    <motion.div
      className="btml-phone"
      animate={{ boxShadow: shadow }}
      transition={{ duration: 0.2 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: compact ? '0 16px' : '0 20px',
        minHeight: compact ? 48 : 60,
        borderRadius: compact ? 12 : 14,
        border: `1.5px solid ${borderColor}`,
        background: 'var(--bg2)',
        transition: 'border-color 0.2s',
        overflow: 'hidden',
      }}
    >
      <PhoneInput
        international
        defaultCountry="IN"
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="+91 98765 43210"
        style={{ flex: 1, display: 'flex', alignItems: 'center' }}
      />
    </motion.div>
  );
}

// ── TextInput ──────────────────────────────────────────────────────────────
function TextInput({ type, inputMode, placeholder, value, onChange, onKeyDown, validationState, autoFocus, compact }) {
  const [focused, setFocused] = React.useState(false);

  const borderColor = validationState === 'error' ? '#ef4444'
    : validationState === 'valid' ? '#10b981'
    : focused ? 'var(--blue)' : 'var(--border)';

  const shadow = validationState === 'error' ? '0 0 0 4px rgba(239,68,68,0.10)'
    : validationState === 'valid' ? '0 0 0 4px rgba(16,185,129,0.10)'
    : focused ? '0 0 0 4px rgba(79,82,248,0.10)' : '0 0 0 0px rgba(79,82,248,0)';

  return (
    <motion.div animate={{ boxShadow: shadow }} transition={{ duration: 0.2 }} style={{ borderRadius: compact ? 12 : 14 }}>
      <input
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoFocus={autoFocus !== false && !compact}
        style={{
          width: '100%',
          padding: compact ? '12px 16px' : '18px 20px',
          fontSize: compact ? 15 : 20,
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontWeight: 400,
          color: 'var(--text)',
          background: 'var(--bg2)',
          border: `1.5px solid ${borderColor}`,
          borderRadius: compact ? 12 : 14,
          outline: 'none',
          transition: 'border-color 0.2s',
          minHeight: compact ? 48 : 60,
          display: 'block',
          boxSizing: 'border-box',
        }}
      />
    </motion.div>
  );
}

// ── ChipButton (for Page 2 multi-option selectors) ─────────────────────────
function ChipButton({ label, selected, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      style={{
        padding: '8px 16px',
        border: `1.5px solid ${selected ? 'var(--blue)' : 'var(--border)'}`,
        borderRadius: 100,
        background: selected ? 'rgba(79,82,248,0.08)' : '#ffffff',
        color: selected ? 'var(--blue)' : 'var(--text)',
        fontSize: 13,
        fontWeight: selected ? 600 : 400,
        fontFamily: 'inherit',
        cursor: 'pointer',
        transition: 'border-color 0.18s, background 0.18s, color 0.18s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </motion.button>
  );
}

// ── ErrorMessage ───────────────────────────────────────────────────────────
function ErrorMessage({ message }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.2 }}
      style={{ fontSize: 13, color: '#ef4444', marginTop: 6, fontWeight: 500, lineHeight: 1.4 }}
    >
      {message}
    </motion.p>
  );
}

// ── OptionButton (kept for backward compat, used in page 3/4) ──────────────
function OptionButton({ label, selected, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.015, borderColor: 'var(--blue)' }}
      whileTap={{ scale: 0.985 }}
      onClick={onClick}
      style={{
        width: '100%',
        padding: '14px 18px',
        border: `1.5px solid ${selected ? 'var(--blue)' : 'var(--border)'}`,
        borderRadius: 12,
        background: selected ? 'rgba(79,82,248,0.05)' : '#ffffff',
        color: selected ? 'var(--blue)' : 'var(--text)',
        fontSize: 14,
        fontWeight: selected ? 600 : 400,
        fontFamily: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 50,
        transition: 'border-color 0.18s, background 0.18s, color 0.18s',
      }}
    >
      <motion.div
        animate={{ borderColor: selected ? 'var(--blue)' : '#d1d5db', background: selected ? 'var(--blue)' : 'transparent' }}
        transition={{ duration: 0.18 }}
        style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <AnimatePresence>
          {selected && (
            <motion.div
              key="dot"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ duration: 0.15, ease: 'backOut' }}
              style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }}
            />
          )}
        </AnimatePresence>
      </motion.div>
      {label}
    </motion.button>
  );
}

// ── SpinnerIcon ────────────────────────────────────────────────────────────
function SpinnerIcon() {
  return (
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 0.75, repeat: Infinity, ease: 'linear' }}
      style={{ width: 17, height: 17, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#ffffff' }}
    />
  );
}
